import type {
  BimbinganSessionDoc,
  ConsultDoc,
  PersyaratanAnswer,
  PersyaratanDoc,
  PersyaratanItem,
  PersyaratanStage,
  SemproAttendance,
  SkripsiTeamDoc,
} from './types';

/**
 * Pure rule functions for persyaratan resolution, completeness checks and
 * izin-gate evaluation. No hooks, no DB access — safe to unit-test and safe to
 * call inside useMemo. Re-exported from `./data/domains` for call sites that
 * historically imported them from there.
 */

/** The prodi/jurusan/fakultas of a skripsi — how a template is resolved. */
export interface PersyaratanScopeContext {
  prodiId?: string | null;
  jurusanId?: string | null;
  fakultas?: string | null;
}

/**
 * Pick the persyaratan template that applies to a skripsi at a stage.
 * Most specific scope wins: prodi → jurusan → fakultas → global.
 */
export function resolvePersyaratanTemplate(
  templates: PersyaratanDoc[],
  stage: PersyaratanStage | null,
  scope: PersyaratanScopeContext = {},
): PersyaratanDoc | null {
  if (!stage) return null;
  const list = templates.filter((p) => p.stage === stage && p.status === 'active');
  if (list.length === 0) return null;
  if (scope.prodiId) {
    const m = list.find((p) => p.scope === 'prodi' && p.prodiId === scope.prodiId);
    if (m) return m;
  }
  if (scope.jurusanId) {
    const m = list.find((p) => p.scope === 'jurusan' && p.jurusanId === scope.jurusanId);
    if (m) return m;
  }
  if (scope.fakultas) {
    const m = list.find((p) => p.scope === 'fakultas' && p.fakultas === scope.fakultas);
    if (m) return m;
  }
  return list.find((p) => p.scope === 'global') ?? null;
}

/**
 * Which level administers a stage's request flow. Stages whose template is
 * fakultas-scoped are handled at the faculty level (documents require a dekan
 * assignment); everything else stays at the prodi level. With no template the
 * flow stays at the prodi level (the historical default).
 */
export function stageManagedAt(template: PersyaratanDoc | null): 'prodi' | 'fakultas' {
  return template?.scope === 'fakultas' ? 'fakultas' : 'prodi';
}

export function isPersyaratanItemComplete(
  item: PersyaratanItem,
  answers: PersyaratanAnswer[],
): boolean {
  if (!item.required) return true;
  const ans = answers.find((a) => a.itemId === item.id);
  if (!ans) return false;
  if (item.type === 'checkbox') return Boolean(ans.selected && ans.selected.length > 0);
  if (item.type === 'file') return Boolean(ans.file?.url);
  return Boolean(ans.text && ans.text.trim().length > 0);
}

/** Whether every required item of a persyaratan template has been answered. */
export function persyaratanComplete(
  template: PersyaratanDoc | null,
  answers: PersyaratanAnswer[],
): boolean {
  if (!template || template.items.length === 0) return true;
  return template.items.every((item) => isPersyaratanItemComplete(item, answers));
}

export function missingPersyaratanLabels(
  template: PersyaratanDoc | null,
  answers: PersyaratanAnswer[],
): string[] {
  if (!template) return [];
  return template.items
    .filter((item) => !isPersyaratanItemComplete(item, answers))
    .map((item) => item.label);
}

export interface ConsultIzinInfo {
  all: boolean;
  approved: string[];
  rejected: string[];
  missing: string[];
  pending: string[];
}

/**
 * Izin completeness for sempro/sidang/penelitian: every active pembimbing must
 * have an approved izin consult before the gate opens.
 */
export function consultIzinInfo(
  consults: ConsultDoc[],
  team: SkripsiTeamDoc[],
  kind: 'izin_sempro' | 'izin_sidang' | 'izin_penelitian',
): ConsultIzinInfo {
  const pems = team.filter(
    (t) => t.status === 'active' && (t.role === 'pembimbing-1' || t.role === 'pembimbing-2'),
  );
  const approved: string[] = [];
  const rejected: string[] = [];
  const pending: string[] = [];
  for (const p of pems) {
    const latest = consults
      .filter((c) => c.kind === kind && c.dosenId === p.dosenId)
      .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))[0];
    if (!latest) pending.push(p.dosenId);
    else if (latest.status === 'disetujui') approved.push(p.dosenId);
    else if (latest.status === 'ditolak') rejected.push(p.dosenId);
    else pending.push(p.dosenId);
  }
  const missing = [...pending, ...rejected];
  return {
    all: pems.length > 0 && approved.length === pems.length,
    approved,
    rejected,
    missing,
    pending,
  };
}

/**
 * Syarat kegiatan seminar via DHK (Daftar Hadir Kegiatan) — Pedoman Akademik
 * FKIP Untan 2023/2024, ketentuan seminar proposal: "Telah mengikuti seminar
 * menjadi peserta sebanyak 2 kali, notulis sebanyak 1 kali, dan pembahas
 * sebanyak 1 kali yang dibuktikan dengan DHK (Daftar Hadir Kegiatan)."
 */
export const DHK_SEMPRO_REQUIRED: Readonly<SemproAttendance> = {
  peserta: 2,
  notulis: 1,
  pembahas: 1,
};

export interface BimbinganGateInfo {
  /** Sesi bimbingan tipe `proposal` yang sudah acc/selesai — syarat sempro. */
  proposalCount: number;
  /**
   * Akumulasi sesi acc/selesai per pembimbing aktif — syarat ujian skripsi
   * Untan ("telah dibimbing 6–8 kali oleh masing-masing Dosen Pembimbing").
   */
  perPembimbing: { dosenId: string; role: 'pembimbing-1' | 'pembimbing-2'; count: number }[];
}

/**
 * Hitung sesi bimbingan yang sudah disetujui dosen (status acc/selesai):
 * total tipe proposal untuk gate sempro, dan per-orang pembimbing aktif
 * untuk gate sidang. Purus — tidak menyentuh DB, aman diunit-test.
 */
export function bimbinganCounts(
  sessions: BimbinganSessionDoc[],
  team: SkripsiTeamDoc[],
): BimbinganGateInfo {
  const approved = sessions.filter((s) => s.status === 'acc' || s.status === 'selesai');
  const proposalCount = approved.filter((s) => s.type === 'proposal').length;
  const perPembimbing = team
    .filter(
      (t): t is SkripsiTeamDoc & { role: 'pembimbing-1' | 'pembimbing-2' } =>
        t.status === 'active' && (t.role === 'pembimbing-1' || t.role === 'pembimbing-2'),
    )
    .map((t) => ({
      dosenId: t.dosenId,
      role: t.role,
      count: approved.filter((s) => s.dosenId === t.dosenId).length,
    }));
  return { proposalCount, perPembimbing };
}

export interface DhkSemproInfo {
  met: boolean;
  /** Label kekurangan, e.g. "peserta 1/2" — langsung tampil di pesan gate. */
  missing: string[];
}

/** Evaluasi bukti DHK mahasiswa terhadap syarat pedoman (peserta 2 / notulis 1 / pembahas 1). */
export function dhkSemproInfo(att: SemproAttendance | undefined): DhkSemproInfo {
  if (!att) {
    return {
      met: false,
      missing: Object.entries(DHK_SEMPRO_REQUIRED).map(([k, n]) => `${k} 0/${n}`),
    };
  }
  const missing: string[] = [];
  for (const [k, n] of Object.entries(DHK_SEMPRO_REQUIRED) as [keyof SemproAttendance, number][]) {
    if (att[k] < n) missing.push(`${k} ${att[k]}/${n}`);
  }
  return { met: missing.length === 0, missing };
}
